import { useMemo } from 'react'
import { API_HOST } from '../constants'
import { createURLQueryString, fetched } from '../helpers/util'
import { FRONTEER_JWT, useAuth } from './auth.hook'
import { useStorage } from '@fold-dev/core'

export const useProjectUnreads = () => {
    const get = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/project-unreads`,
            method: 'GET',
        })
    }

    const remove = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-unreads`,
            method: 'DELETE',
        })
    }

    return {
        get,
        remove,
    }
}

export const useThreads = () => {
    const create = async (workspaceId, projectId, projectMessageId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/threads`,
            method: 'POST',
            body: {},
        })
    }

    const update = async (workspaceId, threadId, updatedThread) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/threads/${threadId}`,
            method: 'PUT',
            body: { ...updatedThread },
        })
    }

    const getWorkspace = async (workspaceId, page = 0) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/threads?page=${page}`,
            method: 'GET',
        })
    }

    const get = async (workspaceId, projectId, projectMessageId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/threads`,
            method: 'GET',
        })
    }

    const remove = async (workspaceId, threadId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/threads/${threadId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        get,
        getWorkspace,
        update,
        remove,
    }
}

export const useProjectMessageAttachments = () => {
    const getAll = async (workspaceId, projectId, projectMessageId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-attachments`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, projectMessageId, projectMessageAttachmentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-attachments/${projectMessageAttachmentId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, projectMessageId, { name, url, meta }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-attachments`,
            method: 'POST',
            body: { name, url, meta },
        })
    }

    const update = async (
        workspaceId,
        projectId,
        projectMessageId,
        projectMessageAttachmentId,
        { name, url, meta }
    ) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-attachments/${projectMessageAttachmentId}`,
            method: 'PUT',
            body: { name, url, meta },
        })
    }

    const remove = async (workspaceId, projectId, projectMessageId, projectMessageAttachmentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-attachments/${projectMessageAttachmentId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useProjectMessageReactions = () => {
    const create = async (workspaceId, projectId, projectMessageId, { id, reaction }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-reactions`,
            method: 'POST',
            body: { id, reaction },
        })
    }

    const remove = async (workspaceId, projectId, projectMessageId, projectMessageReactionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}/project-message-reactions/${projectMessageReactionId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        remove,
    }
}

export const useProjectMessages = () => {
    const getAll = async (workspaceId, projectId, page = 0) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages?page=${page}`,
            method: 'GET',
        })
    }

    const getThread = async (workspaceId, projectId, threadId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${threadId}/thread`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, projectMessageId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, message) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages`,
            method: 'POST',
            body: { ...message },
        })
    }

    const update = async (workspaceId, projectId, projectMessageId, message) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}`,
            method: 'PUT',
            body: { ...message },
        })
    }

    const remove = async (workspaceId, projectId, projectMessageId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-messages/${projectMessageId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        getThread,
        update,
        remove,
    }
}

export const useProjectSections = () => {
    const getAll = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-sections`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, projectSectionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-sections/${projectSectionId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-sections`,
            method: 'POST',
            body: { ...rest },
        })
    }

    const update = async (workspaceId, projectId, projectSectionId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-sections/${projectSectionId}`,
            method: 'PUT',
            body: { ...rest },
        })
    }

    const remove = async (workspaceId, projectId, projectSectionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-sections/${projectSectionId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useProjectUsers = () => {
    const getPreview = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users/preview`,
            method: 'GET',
        })
    }

    const getSearch = async (workspaceId, projectId, query) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users/search?query=${query}`,
            method: 'GET',
        })
    }

    const getAll = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, projectUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users/${projectUserId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, { users, sync = true }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users`,
            method: 'POST',
            body: { users, sync },
        })
    }

    const update = async (workspaceId, projectId, projectUserId, payload) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users/${projectUserId}`,
            method: 'PUT',
            body: { ...payload },
        })
    }

    const remove = async (workspaceId, projectId, projectUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users/${projectUserId}`,
            method: 'DELETE',
        })
    }

    const removeAll = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/project-users`,
            method: 'DELETE',
        })
    }

    return {
        getPreview,
        getSearch,
        create,
        getOne,
        getAll,
        update,
        remove,
        removeAll,
    }
}

export const useProjects = () => {
    const generate = async (workspaceId, projectId, text) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/ai`,
            method: 'POST',
            body: { text },
        })
    }

    const join = async ({ shortcode, id, workspaceId, teamId }) => {
        return await fetched({
            url: `${API_HOST}/projects-join/${shortcode}`,
            method: 'POST',
            body: { id, workspaceId, teamId },
        })
    }

    const roadmap = async ({ shortcode }) => {
        return await fetched({
            url: `${API_HOST}/projects-roadmap/${shortcode}`,
            method: 'GET',
        })
    }

    const shortcode = async ({ shortcode }) => {
        return await fetched({
            url: `${API_HOST}/projects-shortcode/${shortcode}`,
            method: 'GET',
        })
    }

    const getAll = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects`,
            method: 'GET',
        })
    }

    const getStarred = async (workspaceId, starred) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/starred`,
            method: 'POST',
            body: { starred },
        })
    }

    const getMyProjects = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/my-projects`,
            method: 'GET',
        })
    }

    const getMyConversations = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/my-conversations`,
            method: 'GET',
        })
    }

    const getMyProjectsAndConversations = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/my-projects-conversations`,
            method: 'GET',
        })
    }

    const getJoined = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/joined`,
            method: 'GET',
        })
    }

    const getTeam = async (workspaceId, teamId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/team/${teamId}`,
            method: 'GET',
        })
    }

    const getArchived = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/archived`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, { users, ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects`,
            method: 'POST',
            body: { users, ...rest },
        })
    }

    const update = async (workspaceId, projectId, payload: any) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}`,
            method: 'PUT',
            body: { ...payload },
        })
    }

    const remove = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}`,
            method: 'DELETE',
        })
    }

    return {
        generate,
        join,
        shortcode,
        roadmap,
        create,
        getOne,
        getAll,
        getJoined,
        getStarred,
        getMyProjects,
        getMyConversations,
        getMyProjectsAndConversations,
        getTeam,
        getArchived,
        update,
        remove,
    }
}

export const useLabels = () => {
    const getAll = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/labels`,
            method: 'GET',
        })
    }

    const getSearch = async (workspaceId, query) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/labels/search?query=${query}`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, labelId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/labels/${labelId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/labels`,
            method: 'POST',
            body: { ...rest },
        })
    }

    const update = async (workspaceId, labelId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/labels/${labelId}`,
            method: 'PUT',
            body: { ...rest },
        })
    }

    const remove = async (workspaceId, labelId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/labels/${labelId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        getSearch,
        update,
        remove,
    }
}

export const useTaskCommentAttachments = () => {
    const getAll = async (workspaceId, projectId, taskId, taskCommentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}/task-comment-attachments`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, taskId, taskCommentId, taskCommentAttachmentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}/task-comment-attachments/${taskCommentAttachmentId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, taskId, taskCommentId, { name, url, meta }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}/task-comment-attachments`,
            method: 'POST',
            body: { name, url, meta },
        })
    }

    const update = async (
        workspaceId,
        projectId,
        taskId,
        taskCommentId,
        taskCommentAttachmentId,
        { name, url, meta }
    ) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}/task-comment-attachments/${taskCommentAttachmentId}`,
            method: 'PUT',
            body: { name, url, meta },
        })
    }

    const remove = async (workspaceId, projectId, taskId, taskCommentId, taskCommentAttachmentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}/task-comment-attachments/${taskCommentAttachmentId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useTaskCommentReactions = () => {
    const create = async (taskCommentId, { id, reaction }) => {
        return await fetched({
            url: `${API_HOST}/task-comments/${taskCommentId}/task-comment-reactions`,
            method: 'POST',
            body: { id, reaction },
        })
    }

    const remove = async (taskCommentId, taskCommentReactionId) => {
        return await fetched({
            url: `${API_HOST}/task-comments/${taskCommentId}/task-comment-reactions/${taskCommentReactionId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        remove,
    }
}

export const useTaskAttachments = () => {
    const getAll = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-attachments`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, taskId, taskAttachmentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-attachments/${taskAttachmentId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, taskId, attachments) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-attachments`,
            method: 'POST',
            body: { attachments },
        })
    }

    const update = async (workspaceId, projectId, taskId, taskAttachmentId, { name, url, meta }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-attachments/${taskAttachmentId}`,
            method: 'PUT',
            body: { name, url, meta },
        })
    }

    const remove = async (workspaceId, projectId, taskId, taskAttachmentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-attachments/${taskAttachmentId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useTaskComments = () => {
    const getAll = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, taskId, taskCommentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, taskId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments`,
            method: 'POST',
            body: { ...rest },
        })
    }

    const update = async (workspaceId, projectId, taskId, taskCommentId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}`,
            method: 'PUT',
            body: { ...rest },
        })
    }

    const remove = async (workspaceId, projectId, taskId, taskCommentId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-comments/${taskCommentId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useTaskLabels = () => {
    const getAll = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-labels`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, taskId, { id, labelId }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-labels`,
            method: 'POST',
            body: { id, labelId },
        })
    }

    const remove = async (workspaceId, projectId, taskId, taskLabelId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-labels/${taskLabelId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getAll,
        remove,
    }
}

export const useTaskLikes = () => {
    const create = async (taskId) => {
        return await fetched({
            url: `${API_HOST}/task-likes/${taskId}`,
            method: 'POST',
            body: {},
        })
    }

    const remove = async (taskId) => {
        return await fetched({
            url: `${API_HOST}/task-likes/${taskId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        remove,
    }
}

export const useTaskFollowers = () => {
    const create = async (workspaceId, projectId, taskId, { userId }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-followers`,
            method: 'POST',
            body: { userId },
        })
    }

    const remove = async (workspaceId, projectId, taskId, taskFollowerId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-followers/${taskFollowerId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        remove,
    }
}

export const useTaskUsers = () => {
    const create = async (workspaceId, projectId, taskId, { id, userId }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-users`,
            method: 'POST',
            body: { id, userId },
        })
    }

    const remove = async (workspaceId, projectId, taskId, taskUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/task-users/${taskUserId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        remove,
    }
}

export const useTasks = () => {
    const getAll = async (workspaceId, projectId, filters) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks?${createURLQueryString(filters)}`,
            method: 'GET',
            abortable: false,
        })
    }

    const shortcode = async ({ shortcode }) => {
        return await fetched({
            url: `${API_HOST}/tasks-shortcode/${shortcode}`,
            method: 'GET',
        })
    }

    const getNextPosition = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/next-position`,
            method: 'GET',
        })
    }

    const getSubtasks = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/subtasks`,
            method: 'GET',
        })
    }

    const getUnassigned = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/unassigned`,
            method: 'GET',
        })
    }

    const getToday = async (workspaceId, startDate, endDate) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/date-assigned?${createURLQueryString({
                startDate,
                endDate,
            })}`,
            method: 'GET',
        })
    }

    const getUpcoming = async (workspaceId, startDate, endDate) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/date-assigned?${createURLQueryString({
                startDate,
                endDate,
            })}`,
            method: 'GET',
        })
    }

    const getCalendar = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/calendar`,
            method: 'GET',
        })
    }

    const getDateAssigned = async (workspaceId, startDate, endDate) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/date-assigned?${createURLQueryString({
                startDate,
                endDate,
            })}`,
            method: 'GET',
        })
    }

    const getDate = async (workspaceId, startDate, endDate) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/date?${createURLQueryString({
                startDate,
                endDate,
            })}`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/tasks/${taskId}`,
            method: 'GET',
        })
    }

    const getHistory = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/history`,
            method: 'GET',
        })
    }

    const createv2 = async (workspaceId, projectId, task) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/v2`,
            method: 'POST',
            body: task,
        })
    }

    const createMultiple = async (workspaceId, projectId, projectSectionId, parentTaskId, tasks) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/multiple`,
            method: 'POST',
            body: { projectSectionId, parentTaskId, tasks },
        })
    }

    const create = async (workspaceId, projectId, task) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks`,
            method: 'POST',
            body: task,
        })
    }

    const update = async (workspaceId, projectId, taskId, task) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}`,
            method: 'PUT',
            body: task,
        })
    }

    const updatev2 = async (workspaceId, projectId, taskId, task) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/v2`,
            method: 'PUT',
            body: task,
        })
    }

    const moveToProject = async (workspaceId, projectId, tasks) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/move-to-project`,
            method: 'PUT',
            body: { tasks },
        })
    }

    const updateManyFromMove = async (workspaceId, projectId, tasks) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/many-from-move`,
            method: 'PUT',
            body: { tasks },
        })
    }

    const updateMany = async (workspaceId, projectId, tasks) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/many`,
            method: 'PUT',
            body: { tasks },
        })
    }

    const remove = async (workspaceId, projectId, taskId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}`,
            method: 'DELETE',
        })
    }

    const removeMany = async (workspaceId, projectId, taskIds) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/delete-many`,
            method: 'POST',
            body: { taskIds },
        })
    }

    return {
        create,
        createv2,
        createMultiple,
        shortcode,
        getOne,
        getSubtasks,
        getCalendar,
        getToday,
        getUpcoming,
        getHistory,
        getNextPosition,
        getAll,
        getUnassigned,
        getDate,
        getDateAssigned,
        update,
        updatev2,
        updateMany,
        updateManyFromMove,
        moveToProject,
        remove,
        removeMany,
    }
}

export const useTeamUsers = () => {
    const getPreview = async (workspaceId, teamId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users-preview`,
            method: 'GET',
        })
    }

    const getAll = async (workspaceId, teamId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users`,
            method: 'GET',
        })
    }

    const getSearch = async (workspaceId, teamId, query) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users/search?query=${query}`,
            method: 'GET',
        })
    }

    // not used
    const getOne = async (workspaceId, teamId, teamUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users/${teamUserId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, teamId, user) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users`,
            method: 'POST',
            body: user,
        })
    }

    const update = async (workspaceId, teamId, teamUserId, { userId, role }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users/${teamUserId}`,
            method: 'PUT',
            body: { userId, role },
        })
    }

    const remove = async (workspaceId, teamId, teamUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}/team-users/${teamUserId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getPreview,
        getSearch,
        getAll,
        update,
        remove,
    }
}

export const useTeams = () => {
    const join = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams-join`,
            method: 'POST',
        })
    }

    const create = async (workspaceId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams`,
            method: 'POST',
            body: { ...rest },
        })
    }

    const getOne = async (workspaceId, teamId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}`,
            method: 'GET',
        })
    }

    const getAll = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams`,
            method: 'GET',
        })
    }

    const update = async (workspaceId, teamId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}`,
            method: 'PUT',
            body: { ...rest },
        })
    }

    const remove = async (workspaceId, teamId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/teams/${teamId}`,
            method: 'DELETE',
        })
    }

    return {
        join,
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useTools = () => {
    const tenorGet = async (id) => {
        return await fetched({
            url: `${API_HOST}/tools/tenor/${id}`,
            method: 'GET',
        })
    }

    const tenorSearch = async ({ search }) => {
        return await fetched({
            url: `${API_HOST}/tools/tenor?search=${search}`,
            method: 'GET',
        })
    }

    const og = async ({ url }) => {
        return await fetched({
            url: `${API_HOST}/tools/og`,
            method: 'POST',
            body: { url },
        })
    }

    return {
        og,
        tenorSearch,
        tenorGet,
    }
}

export const useWorkspaceInvites = () => {
    const getAll = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-invites`,
            method: 'GET',
        })
    }

    const redeem = async (workspaceId, invite) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-invites/redeem?invite=${invite}`,
            method: 'GET',
        })
    }

    const resend = async (workspaceId, workspaceInviteId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-invites/${workspaceInviteId}/resend`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, workspaceInviteId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-invites/${workspaceInviteId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, { emails }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-invites`,
            method: 'POST',
            body: { emails },
        })
    }

    const remove = async (workspaceId, workspaceInviteId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-invites/${workspaceInviteId}`,
            method: 'DELETE',
        })
    }

    return {
        resend,
        redeem,
        create,
        getOne,
        getAll,
        remove,
    }
}

export const useWorkspaceUsers = () => {
    const create = async (workspaceId, { email }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-users`,
            method: 'POST',
            body: { email },
        })
    }

    const getOne = async (workspaceId, workspaceUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-users/${workspaceUserId}`,
            method: 'GET',
        })
    }

    const getSearch = async (workspaceId, query) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-users/search`,
            method: 'GET',
            query: { query },
        })
    }

    const getAll = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-users`,
            method: 'GET',
        })
    }

    const update = async (workspaceId, workspaceUserId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-users/${workspaceUserId}`,
            method: 'PUT',
            body: { ...rest },
        })
    }

    const remove = async (workspaceId, workspaceUserId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/workspace-users/${workspaceUserId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        getSearch,
        update,
        remove,
    }
}

export const useWorkspaces = () => {
    const slug = async ({ slug }) => {
        return await fetched({
            url: `${API_HOST}/workspaces-slug/${slug}`,
            method: 'GET',
        })
    }

    const join = async ({ externalToken }) => {
        return await fetched({
            url: `${API_HOST}/workspaces-join/${externalToken}`,
            method: 'POST',
        })
    }

    const create = async ({ name, image }) => {
        return await fetched({
            url: `${API_HOST}/workspaces`,
            method: 'POST',
            body: { name, image },
        })
    }

    const getOnePublic = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/public`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}`,
            method: 'GET',
        })
    }

    const getAll = async () => {
        return await fetched({
            url: `${API_HOST}/workspaces`,
            method: 'GET',
        })
    }

    const update = async (workspaceId, { ...rest }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}`,
            method: 'PUT',
            body: { ...rest },
        })
    }

    const remove = async (workspaceId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}`,
            method: 'DELETE',
        })
    }

    const changeSubscription = async (workspaceId, subscriptionId, payload) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/${subscriptionId}`,
            method: 'PUT',
            body: { ...payload },
        })
    }

    const cancelSubscription = async (workspaceId, subscriptionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/${subscriptionId}`,
            method: 'DELETE',
        })
    }

    const getLemonSqueezyPaymentUrl = async (workspaceId, subscriptionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/ls-payment-details?subscriptionId=${subscriptionId}`,
            method: 'GET',
        })
    }

    return {
        slug,
        join,
        create,
        getOne,
        getOnePublic,
        getAll,
        update,
        remove,
        getLemonSqueezyPaymentUrl,
        changeSubscription,
        cancelSubscription,
    }
}

export const useNotifications = () => {
    const getInbox = async (workspaceId: string, query = {}) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/inbox?${createURLQueryString(query)}`,
            method: 'GET',
        })
    }

    const getAll = async (workspaceId: string, query = {}) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications?${createURLQueryString(query)}`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId: string, notificationId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/${notificationId}`,
            method: 'GET',
        })
    }

    const updateAll = async (workspaceId: string, payload: any) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications`,
            method: 'PUT',
            body: { ...payload },
        })
    }

    const updateProject = async (workspaceId: string, projectId: string, payload: any) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/project?projectId=${projectId}`,
            method: 'PUT',
            body: { ...payload },
        })
    }

    const update = async (workspaceId: string, notificationId: string, payload: any) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/${notificationId}`,
            method: 'PUT',
            body: { ...payload },
        })
    }

    const removeAll = async (workspaceId: string) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications`,
            method: 'DELETE',
        })
    }

    const remove = async (workspaceId: string, notificationId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/${notificationId}`,
            method: 'DELETE',
        })
    }

    const removeProject = async (workspaceId: string, projectId: string) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/project?projectId=${projectId}`,
            method: 'DELETE',
        })
    }

    const removeProjectMessageMention = async (workspaceId: string, projectId: string) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/notifications/project-message-mention?projectId=${projectId}`,
            method: 'DELETE',
        })
    }

    return {
        update,
        updateProject,
        updateAll,
        getInbox,
        getAll,
        getOne,
        remove,
        removeProject,
        removeAll,
        removeProjectMessageMention,
    }
}

export const useUserEmails = () => {
    const create = async ({ email }) => {
        return await fetched({
            url: `${API_HOST}/user-emails`,
            method: 'POST',
            body: { email },
        })
    }

    const resend = async ({ userEmailId }) => {
        return await fetched({
            url: `${API_HOST}/user-emails/resend`,
            method: 'PUT',
            body: { userEmailId },
        })
    }

    const remove = async ({ userEmailId }) => {
        return await fetched({
            url: `${API_HOST}/user-emails/${userEmailId}`,
            method: 'DELETE',
        })
    }

    const confirm = async ({ token }) => {
        return await fetched({
            url: `${API_HOST}/user-emails/confirm?token=${token}`,
            method: 'GET',
        })
    }

    return {
        create,
        resend,
        remove,
        confirm,
    }
}

export const useCustomFields = () => {
    const getAll = async (workspaceId, projectId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, customFieldId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, { text, description, color }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields`,
            method: 'POST',
            body: { text, description, color },
        })
    }

    const update = async (workspaceId, projectId, customFieldId, { text, description, color }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}`,
            method: 'PUT',
            body: { text, description, color },
        })
    }

    const remove = async (workspaceId, projectId, customFieldId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useCustomFieldOptions = () => {
    const getAll = async (workspaceId, projectId, customFieldId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}/custom-field-options`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, customFieldId, customFieldOptionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}/custom-field-options/${customFieldOptionId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, customFieldId, { text, description, color }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}/custom-field-options`,
            method: 'POST',
            body: { text, description, color },
        })
    }

    const update = async (workspaceId, projectId, customFieldId, customFieldOptionId, { text, description, color }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}/custom-field-options/${customFieldOptionId}`,
            method: 'PUT',
            body: { text, description, color },
        })
    }

    const remove = async (workspaceId, projectId, customFieldId, customFieldOptionId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/custom-fields/${customFieldId}/custom-field-options/${customFieldOptionId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}

export const useCustomFieldValues = () => {
    const getAll = async (workspaceId, projectId, taskId, customFieldId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/custom-fields/${customFieldId}/custom-field-values`,
            method: 'GET',
        })
    }

    const getOne = async (workspaceId, projectId, taskId, customFieldId, customFieldValueId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/custom-fields/${customFieldId}/custom-field-values/${customFieldValueId}`,
            method: 'GET',
        })
    }

    const create = async (workspaceId, projectId, taskId, customFieldId, { text, description, color }) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/custom-fields/${customFieldId}/custom-field-values`,
            method: 'POST',
            body: { text, description, color },
        })
    }

    const update = async (
        workspaceId,
        projectId,
        taskId,
        customFieldId,
        customFieldValueId,
        { text, description, color }
    ) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/custom-fields/${customFieldId}/custom-field-values/${customFieldValueId}`,
            method: 'PUT',
            body: { text, description, color },
        })
    }

    const remove = async (workspaceId, projectId, taskId, customFieldId, customFieldValueId) => {
        return await fetched({
            url: `${API_HOST}/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/custom-fields/${customFieldId}/custom-field-values/${customFieldValueId}`,
            method: 'DELETE',
        })
    }

    return {
        create,
        getOne,
        getAll,
        update,
        remove,
    }
}
